import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { PageToolbar, EmptyState, StatusPill } from "@/components/PageToolbar";
import CrudDialog from "@/components/CrudDialog";
import ImportButton from "@/components/ImportButton";
import { useAuth } from "@/context/AuthContext";
import Pagination, { usePagination } from "@/components/Pagination";

export default function Customers() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { page, setPage, pageRows, totalPages, total, pageSize, setPageSize } = usePagination(rows, 50, q);

  const load = async () => {
    const { data } = await api.get("/customers", { params: q ? { q } : {} });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const fields = [
    { name: "account", label: "Account / Name", required: true },
    { name: "mobile", label: "Mobile", type: "mobile", required: true },
    { name: "email", label: "Email", type: "email" },
    { name: "owner", label: "Owner" },
    { name: "display_name", label: "Display Name" },
    { name: "gst", label: "GST" },
    { name: "pan", label: "PAN" },
    { name: "fssai", label: "FSSAI" },
    { name: "cin", label: "CIN" },
    { name: "address", label: "Address", type: "textarea", full: true },
    { name: "active", label: "Status", type: "boolean" },
  ];

  const save = async (form) => {
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, { ...editing, ...form });
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer created");
      }
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.account}"?`)) return;
    try {
      await api.delete(`/customers/${r.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="customers-page">
      <PageToolbar
        onNew={() => { setEditing(null); setOpen(true); }}
        newLabel="New Customer"
        exportName={isAdmin ? "customers" : null}
        searchValue={q}
        onSearchChange={setQ}
        extra={isAdmin ? <ImportButton resource="customers" onDone={load} /> : null}
      />
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm crm-table">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold w-10">Sr.</th>
                <th className="px-3 py-2.5 text-left font-semibold">Account</th>
                <th className="px-3 py-2.5 text-left font-semibold">Mobile</th>
                <th className="px-3 py-2.5 text-left font-semibold">GST</th>
                <th className="px-3 py-2.5 text-left font-semibold">PAN</th>
                <th className="px-3 py-2.5 text-left font-semibold">Email</th>
                <th className="px-3 py-2.5 text-left font-semibold">Owner</th>
                <th className="px-3 py-2.5 text-left font-semibold">Status</th>
                <th className="px-3 py-2.5 text-right font-semibold w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageRows.map((r, i) => (
                <tr key={r.id} data-testid="customer-row">
                  <td className="px-3 py-2 text-slate-500 font-mono-num">{(page - 1) * 50 + i + 1}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{r.account}</td>
                  <td className="px-3 py-2 font-mono-num">{r.mobile}</td>
                  <td className="px-3 py-2 font-mono-num text-slate-600">{r.gst}</td>
                  <td className="px-3 py-2 font-mono-num text-slate-600">{r.pan}</td>
                  <td className="px-3 py-2 text-slate-600">{r.email}</td>
                  <td className="px-3 py-2 text-slate-600">{r.owner}</td>
                  <td className="px-3 py-2"><StatusPill active={r.active !== false} /></td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button data-testid="edit-btn" onClick={() => { setEditing(r); setOpen(true); }} className="p-1.5 hover:bg-slate-100 rounded text-[color:var(--brand-teal)]"><Pencil className="h-4 w-4" /></button>
                      <button data-testid="delete-btn" onClick={() => del(r)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <EmptyState label="No customers yet" />}
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} setPageSize={setPageSize} testid="customers-pagination" />
      </div>
      <CrudDialog
        open={open}
        setOpen={setOpen}
        title={editing ? "Edit Customer" : "New Customer"}
        fields={fields}
        value={editing}
        onSave={save}
      />
    </div>
  );
}
